import React, { useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { Tag, Target, Plus, X, Check, AlertCircle, Search, Info } from 'lucide-react';
import useCampagnesEtiquetees from '../../hooks/useCampagnesEtiquetees';
import {
  TYPES_OBJECTIF,
  DEFINITIONS_OBJECTIF,
  estActionnable,
  formaterObjectif,
  objectifDuType
} from '../../utils/objectifsCampagne';

/**
 * Labels et objectifs des campagnes du compte affiché, sans quitter le dashboard.
 *
 * ## Ce que ce panneau remplace
 *
 * La page `#settings` : un sélecteur de compte suivi de cette même table. Pour étiqueter une
 * campagne qu'on avait sous les yeux, il fallait quitter le dashboard, rouvrir un menu,
 * resélectionner le compte qu'on venait de consulter, puis revenir. Le panneau reçoit le compte
 * déjà affiché — il n'y a plus rien à resélectionner.
 *
 * ## Deux familles d'objectifs, et il faut le dire
 *
 * `CPA` et `ROAS` sont **actionnables** : les outils d'enchères les lisent et s'en servent pour
 * recommander. `CPC` et `CONVERSIONS` sont **déclaratifs** : on les affiche et on les compare au
 * réalisé, aucun moteur ne s'en sert. L'écran le signale, sans quoi quelqu'un posera un CPC en
 * croyant piloter ses enchères.
 *
 * Une campagne peut porter un objectif **par type**. Si elle porte à la fois un CPA et un ROAS,
 * les outils d'enchères retiennent le CPA — la règle est fixe, et le panneau l'annonce plutôt que
 * de laisser découvrir l'arbitrage.
 */
function PanneauLabelsObjectifs({ isOpen, onClose, accountId, accountType, nomCompte, token }) {
  const {
    campagnes,
    labelsDisponibles,
    chargement,
    erreur,
    enregistrerLabels,
    enregistrerObjectif,
    supprimerObjectif
  } = useCampagnesEtiquetees({ accountId, accountType, token, actif: isOpen });

  const [recherche, setRecherche] = useState('');
  const [message, setMessage] = useState('');
  const [erreurLocale, setErreurLocale] = useState(null);

  // Édition en cours : une seule cellule à la fois, comme dans l'écran d'origine
  const [campagneEnEdition, setCampagneEnEdition] = useState(null);
  const [champEnEdition, setChampEnEdition] = useState(null);   // 'labels' | 'objectifs'
  const [labelsTemporaires, setLabelsTemporaires] = useState([]);
  const [nouveauLabel, setNouveauLabel] = useState('');
  const [nouvelObjectif, setNouvelObjectif] = useState({ type: 'CPA', valeur: '' });

  const campagnesFiltrees = useMemo(() => {
    const terme = recherche.trim().toLowerCase();
    if (!terme) return campagnes;
    return campagnes.filter(c => (c.campaignName || '').toLowerCase().includes(terme));
  }, [campagnes, recherche]);

  const annulerEdition = () => {
    setCampagneEnEdition(null);
    setChampEnEdition(null);
    setLabelsTemporaires([]);
    setNouveauLabel('');
    setNouvelObjectif({ type: 'CPA', valeur: '' });
  };

  const confirmer = (texte) => {
    setMessage(texte);
    setTimeout(() => setMessage(''), 3000);
  };

  // ------------------------------------------------------------------ labels

  const ouvrirLabels = (campagne) => {
    setCampagneEnEdition(campagne.campaignId);
    setChampEnEdition('labels');
    setLabelsTemporaires([...(campagne.labels || [])]);
    setNouveauLabel('');
  };

  const ajouterLabel = (valeur) => {
    const label = String(valeur || '').trim().toLowerCase();
    if (!label || labelsTemporaires.includes(label)) return;
    setLabelsTemporaires([...labelsTemporaires, label]);
    setNouveauLabel('');
  };

  const enregistrerLesLabels = async () => {
    /**
     * Un label tapé mais non validé par « + » est quand même enregistré.
     *
     * Comportement repris de l'écran d'origine, et conservé délibérément : sans lui, taper un
     * label puis cliquer directement sur « Enregistrer » le perdait en silence.
     */
    const aEnregistrer = [...labelsTemporaires];
    const enAttente = nouveauLabel.trim().toLowerCase();
    if (enAttente && !aEnregistrer.includes(enAttente)) aEnregistrer.push(enAttente);

    try {
      setErreurLocale(null);
      await enregistrerLabels(campagneEnEdition, aEnregistrer);
      annulerEdition();
      confirmer('Labels enregistrés');
    } catch (e) {
      setErreurLocale(e.message);
    }
  };

  // ------------------------------------------------------------------ objectifs

  const ouvrirObjectifs = (campagne) => {
    setCampagneEnEdition(campagne.campaignId);
    setChampEnEdition('objectifs');
    // Proposer un type encore libre plutôt qu'un type déjà posé, qu'on écraserait sans le dire
    const dejaPoses = (campagne.objectifs || []).map(o => o.objectiveType);
    const libre = TYPES_OBJECTIF.find(t => !dejaPoses.includes(t)) || 'CPA';
    setNouvelObjectif({ type: libre, valeur: '' });
  };

  const enregistrerLObjectif = async () => {
    const valeur = parseFloat(String(nouvelObjectif.valeur).replace(',', '.'));
    if (!Number.isFinite(valeur) || valeur <= 0) {
      setErreurLocale('Indiquez une valeur positive');
      return;
    }
    if (nouvelObjectif.type === 'CONVERSIONS' && !Number.isInteger(valeur)) {
      setErreurLocale('Un objectif de conversions doit être un nombre entier');
      return;
    }
    try {
      setErreurLocale(null);
      await enregistrerObjectif(campagneEnEdition, nouvelObjectif.type, valeur);
      annulerEdition();
      confirmer('Objectif enregistré');
    } catch (e) {
      setErreurLocale(e.message);
    }
  };

  const retirerObjectif = async (campaignId, objectiveType) => {
    try {
      setErreurLocale(null);
      await supprimerObjectif(campaignId, objectiveType);
      confirmer('Objectif supprimé');
    } catch (e) {
      setErreurLocale(e.message);
    }
  };

  if (!isOpen) return null;

  const styleBouton = {
    padding: '6px 12px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    backgroundColor: 'white',
    fontSize: '12px',
    cursor: 'pointer'
  };
  const stylePrincipal = { ...styleBouton, backgroundColor: '#111827', color: 'white', border: 'none' };

  return ReactDOM.createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '24px'
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '0.75rem',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          width: '100%',
          maxWidth: '1000px',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* En-tête */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '18px 22px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Tag size={18} />
            <div>
              <div style={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>
                Labels et objectifs des campagnes
              </div>
              {nomCompte && (
                <div style={{ fontSize: '12px', color: '#6b7280' }}>{nomCompte}</div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {message && (
              <span style={{ color: '#059669', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Check size={15} />{message}
              </span>
            )}
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Corps */}
        <div style={{ padding: '18px 22px', overflowY: 'auto', flex: 1 }}>
          {(erreur || erreurLocale) && (
            <div style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              padding: '10px 12px',
              marginBottom: '14px',
              color: '#dc2626',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <AlertCircle size={15} />
              {erreurLocale || erreur}
              <button
                onClick={() => setErreurLocale(null)}
                style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <X size={13} />
              </button>
            </div>
          )}

          {chargement && campagnes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              Chargement des campagnes…
            </div>
          ) : campagnes.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '36px', color: '#6b7280',
              backgroundColor: '#FAF9F5', borderRadius: '6px', fontSize: '13px', lineHeight: 1.6
            }}>
              <p style={{ margin: '0 0 8px 0' }}>Aucune campagne enregistrée pour ce compte.</p>
              <p style={{ margin: 0 }}>
                Les campagnes s'enregistrent à l'affichage du dashboard. Rechargez la page si le
                tableau des campagnes est bien rempli.
              </p>
            </div>
          ) : (
            <>
              {/* Recherche + rappel des labels du compte */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
                {/* `minWidth: 0` : un élément de flexbox refuse par défaut de descendre sous la
                    largeur de son contenu, et pousse le conteneur au lieu de se rétrécir. */}
                <div style={{ position: 'relative', flex: '1 1 220px', minWidth: 0 }}>
                  <Search size={14} style={{ position: 'absolute', left: 10, top: 9, color: '#9ca3af' }} />
                  <input
                    type="text"
                    value={recherche}
                    onChange={(e) => setRecherche(e.target.value)}
                    placeholder="Rechercher une campagne…"
                    style={{
                      width: '100%', padding: '7px 10px 7px 30px',
                      border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px',
                      /* `box-sizing` n'est pas posé globalement dans cette application : il ne
                         l'est que sur quelques sélecteurs d'`App.css`. Sans cette ligne, les
                         40 px de padding et de bordure s'ajoutent aux 100 % de largeur, le champ
                         dépasse la modale et un ascenseur horizontal apparaît. */
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                {labelsDisponibles.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>Labels du compte :</span>
                    {labelsDisponibles.map(label => (
                      <span key={label} style={{
                        padding: '3px 9px', backgroundColor: '#e0f2fe', color: '#0369a1',
                        borderRadius: '10px', fontSize: '11px', fontWeight: 500
                      }}>{label}</span>
                    ))}
                  </div>
                )}
              </div>

              <div style={{
                display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px',
                fontSize: '12px', color: '#6b7280', backgroundColor: '#FAF9F5',
                padding: '8px 10px', borderRadius: '6px'
              }}>
                <Info size={14} />
                <span>
                  Le CPA et le ROAS alimentent l'Optimiseur d'enchères ; le CPC et les conversions
                  sont indicatifs.
                </span>
              </div>

              {/* Un nom de campagne très long ne doit pas élargir la modale : le tableau est
                  borné, et les cellules coupent plutôt que de pousser. */}
              <table style={{ width: '100%', maxWidth: '100%', tableLayout: 'fixed', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ textAlign: 'left', padding: '10px 8px', fontSize: '12px', fontWeight: 600, color: '#374151' }}>
                      Campagne
                    </th>
                    <th style={{ textAlign: 'left', padding: '10px 8px', fontSize: '12px', fontWeight: 600, color: '#374151', width: '34%' }}>
                      Labels
                    </th>
                    <th style={{ textAlign: 'left', padding: '10px 8px', fontSize: '12px', fontWeight: 600, color: '#374151', width: '32%' }}>
                      Objectifs
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {campagnesFiltrees.map((campagne, index) => {
                    const enEdition = campagneEnEdition === campagne.campaignId;
                    const objectifs = campagne.objectifs || [];

                    return (
                      <tr key={campagne.campaignId} style={{
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: index % 2 === 0 ? 'white' : '#FAF9F5'
                      }}>
                        <td style={{
                          padding: '10px 8px', fontSize: '13px', color: '#374151',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                        }} title={campagne.campaignName}>
                          {campagne.campaignName}
                        </td>

                        {/* Labels */}
                        <td style={{ padding: '10px 8px' }}>
                          {enEdition && champEnEdition === 'labels' ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                {labelsTemporaires.map(label => (
                                  <span key={label} style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                                    padding: '3px 8px', backgroundColor: '#dbeafe', color: '#1d4ed8',
                                    borderRadius: '10px', fontSize: '11px'
                                  }}>
                                    {label}
                                    <button
                                      onClick={() => setLabelsTemporaires(labelsTemporaires.filter(l => l !== label))}
                                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}
                                    >
                                      <X size={11} color="#1d4ed8" />
                                    </button>
                                  </span>
                                ))}
                              </div>
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <input
                                  type="text"
                                  value={nouveauLabel}
                                  onChange={(e) => setNouveauLabel(e.target.value)}
                                  onKeyDown={(e) => { if (e.key === 'Enter') ajouterLabel(nouveauLabel); }}
                                  placeholder="Nouveau label…"
                                  autoFocus
                                  style={{ flex: 1, minWidth: 0, boxSizing: 'border-box', padding: '5px 9px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px' }}
                                />
                                <button onClick={() => ajouterLabel(nouveauLabel)} style={styleBouton}>
                                  <Plus size={12} />
                                </button>
                              </div>
                              {labelsDisponibles.filter(l => !labelsTemporaires.includes(l)).length > 0 && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                  {labelsDisponibles.filter(l => !labelsTemporaires.includes(l)).map(label => (
                                    <button
                                      key={label}
                                      onClick={() => ajouterLabel(label)}
                                      style={{ ...styleBouton, padding: '2px 8px', fontSize: '11px', color: '#6b7280' }}
                                    >
                                      + {label}
                                    </button>
                                  ))}
                                </div>
                              )}
                              <div style={{ display: 'flex', gap: '6px' }}>
                                <button onClick={enregistrerLesLabels} style={stylePrincipal}>Enregistrer</button>
                                <button onClick={annulerEdition} style={styleBouton}>Annuler</button>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => ouvrirLabels(campagne)}
                              style={{
                                display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px',
                                background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left'
                              }}
                            >
                              {(campagne.labels || []).length === 0 ? (
                                <span style={{ fontSize: '12px', color: '#9ca3af' }}>+ ajouter un label</span>
                              ) : (
                                campagne.labels.map(label => (
                                  <span key={label} style={{
                                    padding: '3px 9px', backgroundColor: '#e0f2fe', color: '#0369a1',
                                    borderRadius: '10px', fontSize: '11px', fontWeight: 500
                                  }}>{label}</span>
                                ))
                              )}
                            </button>
                          )}
                        </td>

                        {/* Objectifs */}
                        <td style={{ padding: '10px 8px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                              {objectifs.map(objectif => (
                                <span
                                  key={objectif.objectiveType}
                                  title={estActionnable(objectif.objectiveType)
                                    ? 'Utilisé par l\'Optimiseur d\'enchères'
                                    : 'Indicatif : aucun outil d\'enchères ne s\'en sert'}
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                                    padding: '3px 9px', borderRadius: '10px', fontSize: '11px', fontWeight: 500,
                                    backgroundColor: estActionnable(objectif.objectiveType) ? '#dcfce7' : '#f3f4f6',
                                    color: estActionnable(objectif.objectiveType) ? '#166534' : '#6b7280'
                                  }}
                                >
                                  {formaterObjectif(objectif)}
                                  <button
                                    onClick={() => retirerObjectif(campagne.campaignId, objectif.objectiveType)}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}
                                  >
                                    <X size={11} />
                                  </button>
                                </span>
                              ))}
                            </div>

                            {enEdition && champEnEdition === 'objectifs' ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                  <select
                                    value={nouvelObjectif.type}
                                    onChange={(e) => setNouvelObjectif({ ...nouvelObjectif, type: e.target.value })}
                                    style={{ padding: '5px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px' }}
                                  >
                                    {/* Tous les types restent proposés : rechoisir un type déjà
                                        posé en modifie la valeur, l'API étant en upsert. Le dire
                                        évite de croire qu'on en ajoute un second. */}
                                    {TYPES_OBJECTIF.map(type => (
                                      <option key={type} value={type}>
                                        {DEFINITIONS_OBJECTIF[type].libelle}
                                        {estActionnable(type) ? '' : ' (indicatif)'}
                                        {objectifDuType(objectifs, type) ? ' — déjà défini' : ''}
                                      </option>
                                    ))}
                                  </select>
                                  <input
                                    type="number"
                                    step={DEFINITIONS_OBJECTIF[nouvelObjectif.type].pas}
                                    min="0"
                                    value={nouvelObjectif.valeur}
                                    onChange={(e) => setNouvelObjectif({ ...nouvelObjectif, valeur: e.target.value })}
                                    onKeyDown={(e) => { if (e.key === 'Enter') enregistrerLObjectif(); }}
                                    autoFocus
                                    style={{ width: '80px', boxSizing: 'border-box', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px' }}
                                  />
                                  <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                    {DEFINITIONS_OBJECTIF[nouvelObjectif.type].unite}
                                  </span>
                                </div>
                                <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                                  {DEFINITIONS_OBJECTIF[nouvelObjectif.type].aide}
                                </div>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                  <button onClick={enregistrerLObjectif} style={stylePrincipal}>Enregistrer</button>
                                  <button onClick={annulerEdition} style={styleBouton}>Annuler</button>
                                </div>
                              </div>
                            ) : (
                              <button
                                onClick={() => ouvrirObjectifs(campagne)}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                                  background: 'none', border: 'none', cursor: 'pointer',
                                  padding: 0, fontSize: '12px', color: '#9ca3af'
                                }}
                              >
                                <Target size={12} />
                                {objectifs.length === 0 ? 'définir un objectif' : 'ajouter un objectif'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {campagnesFiltrees.length === 0 && (
                <div style={{ textAlign: 'center', padding: '24px', color: '#6b7280', fontSize: '13px' }}>
                  Aucune campagne ne correspond à « {recherche} ».
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default PanneauLabelsObjectifs;
